import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/shadcn/components/ui/Dialog';
import { Button } from '~/shadcn/components/ui/Button';
import { Alert, AlertDescription, AlertTitle } from '~/shadcn/components/ui/Alert';
import { AlertTriangle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shadcn/components/ui/Table';

/**
 * Formats a date string to a user-friendly format
 * @param {string} dateString - Date string in format "YYYY-MM-DD HH:mm:ss"
 * @returns {string} Formatted date string like "Jan 15, 2024 at 10:00 AM"
 */
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    } catch (error) {
        return dateString;
    }
};

/**
 * Formats company sources array into a readable string
 * @param {string[]} companySources - Array of company source codes
 * @param {Array} sourceOptions - Array of {value, label} options for mapping
 * @returns {string} Formatted string of company sources
 */
const formatCompanySources = (companySources, sourceOptions = []) => {
    if (!companySources || companySources.length === 0) return 'N/A';
    const labels = companySources.map((source) => {
        const option = sourceOptions.find((opt) => opt.value === source);
        return option ? option.label : source;
    });
    return labels.join(', ');
};

const PartnerCycleCountConflictModal = ({
    open,
    onClose,
    onConfirm,
    conflictData,
    sourceOptions = [],
    isLoading = false,
}) => {
    if (!conflictData || !conflictData.existingRequests) {
        return null;
    }

    const { message, existingRequests } = conflictData;
    const requestCount = existingRequests.length;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="!w-[90vw] !max-w-[90vw] sm:!max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        Cycle Count Conflict Detected
                    </DialogTitle>
                    <DialogDescription>
                        {requestCount} existing cycle count{requestCount !== 1 ? 's' : ''} will be cancelled if you
                        proceed.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <Alert variant="default" className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertTitle className="text-yellow-800 dark:text-yellow-200">Warning</AlertTitle>
                        <AlertDescription className="text-yellow-700 dark:text-yellow-300">{message}</AlertDescription>
                    </Alert>

                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold">Existing Cycle Count Requests:</h3>
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Request ID</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Created Date</TableHead>
                                        <TableHead>Created By</TableHead>
                                        <TableHead>Company Sources</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {existingRequests.map((request) => (
                                        <TableRow key={request.request_id}>
                                            <TableCell className="font-medium">{request.request_id}</TableCell>
                                            <TableCell>
                                                <span
                                                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                                        request.status === 'IN PROGRESS'
                                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                                    }`}
                                                >
                                                    {request.status}
                                                </span>
                                            </TableCell>
                                            <TableCell>{formatDate(request.created_at)}</TableCell>
                                            <TableCell>{request.created_by || 'N/A'}</TableCell>
                                            <TableCell>
                                                {formatCompanySources(request.company_sources, sourceOptions)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={onConfirm} disabled={isLoading} className="bg-yellow-600 hover:bg-yellow-700">
                        {isLoading ? 'Processing...' : 'Confirm & Override'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { PartnerCycleCountConflictModal };
